import React, { useState, useEffect, useCallback } from 'react';
import { Download, Upload, Trash2, MoreVertical, CheckCircle, XCircle, ChevronLeft, ChevronRight, Search, Filter, X, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import type { AccountData, User, AccountStatus } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import JSZip from 'jszip';

type OrderRange = '1-10' | '10-50' | '50-100' | '100-200' | '200-500' | '500+' | 'all';

const AccountsTab: React.FC = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<(AccountData & { creator?: User })[]>([]);
  const [loading, setLoading] = useState(true);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalAccounts, setTotalAccounts] = useState(0);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<AccountStatus | 'all'>('all');
  const [orderRangeFilter, setOrderRangeFilter] = useState<OrderRange>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isFiltering, setIsFiltering] = useState(false);
  const [userFilter, setUserFilter] = useState<string>('all');

  // Fetch users for filter
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .order('email');
        
        if (error) throw error;
        setUsers(data || []);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error('Failed to fetch users');
      }
    };

    fetchUsers();
  }, []);

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchAccounts(currentPage, pageSize);
  }, [currentPage, pageSize, statusFilter, orderRangeFilter, debouncedSearchTerm, userFilter]);

  // Helper function to apply order range filter
  const applyOrderRangeFilter = (query: any, orderRange: OrderRange) => {
    if (orderRange === 'all') return query;
    
    // Parse the range values
    let minOrders: number, maxOrders: number | null;
    
    if (orderRange === '500+') {
      minOrders = 500;
      maxOrders = null; // No upper limit
    } else {
      const [minStr, maxStr] = orderRange.split('-');
      minOrders = parseInt(minStr, 10);
      maxOrders = parseInt(maxStr, 10);
    }
    
    // For 500+ range
    if (maxOrders === null) {
      return query.gte('orders::int', minOrders);
    }
    
    // For ranges like 1-10, 10-50, etc.
    return query
      .gte('orders::int', minOrders)
      .lte('orders::int', maxOrders);
  };

  const fetchAccounts = async (page: number, size: number) => {
    try {
      setLoading(true);
      
      // Build the base query for counting
      let countQuery = supabase.from('accounts').select('*', { count: 'exact', head: true });
      
      // Apply status filter if selected
      if (statusFilter !== 'all') {
        countQuery = countQuery.eq('status', statusFilter);
      }
      
      // Apply order range filter if selected
      if (orderRangeFilter !== 'all') {
        countQuery = applyOrderRangeFilter(countQuery, orderRangeFilter);
      }
      
      // Apply user filter if selected
      if (userFilter !== 'all') {
        countQuery = countQuery.eq('created_by', userFilter);
      }

      // Apply search filter if there's a search term
      if (debouncedSearchTerm) {
        countQuery = countQuery.or(`email.ilike.%${debouncedSearchTerm}%,phone_number.ilike.%${debouncedSearchTerm}%`);
      }
      
      // Execute the count query
      const { count, error: countError } = await countQuery;
      
      if (countError) {
        console.error('Error counting accounts:', countError);
        throw new Error(`Failed to count accounts: ${countError.message}`);
      }
      
      // Update pagination state based on count
      if (count !== null) {
        setTotalAccounts(count);
        setTotalPages(Math.max(1, Math.ceil(count / size)));
      }
      
      // Calculate pagination range
      const from = (page - 1) * size;
      const to = from + size - 1;
      
      // Build the data query with the same filters as the count query
      let dataQuery = supabase.from('accounts').select('*').order('created_at', { ascending: false });
      
      // Apply status filter if selected
      if (statusFilter !== 'all') {
        dataQuery = dataQuery.eq('status', statusFilter);
      }
      
      // Apply order range filter if selected
      if (orderRangeFilter !== 'all') {
        dataQuery = applyOrderRangeFilter(dataQuery, orderRangeFilter);
      }

      // Apply user filter if selected
      if (userFilter !== 'all') {
        dataQuery = dataQuery.eq('created_by', userFilter);
      }
      
      // Apply search filter if there's a search term
      if (debouncedSearchTerm) {
        dataQuery = dataQuery.or(`email.ilike.%${debouncedSearchTerm}%,phone_number.ilike.%${debouncedSearchTerm}%`);
      }
      
      // Apply pagination
      dataQuery = dataQuery.range(from, to);
      
      // Execute the data query
      const { data: accountsData, error: accountsError } = await dataQuery;
      
      if (accountsError) {
        console.error('Error fetching accounts:', accountsError);
        throw new Error(`Failed to fetch accounts: ${accountsError.message}`);
      }
      
      // Ensure all accounts have a status
      const processedAccounts = (accountsData || []).map(account => ({
        ...account,
        status: account.status || 'pending'
      }));
      
      // Get unique creator IDs
      const creatorIds = [...new Set(processedAccounts
        .filter(account => account.created_by)
        .map(account => account.created_by))];
      
      if (creatorIds.length > 0) {
        // Fetch creators
        const { data: creators, error: creatorsError } = await supabase
          .from('users')
          .select('*')
          .in('id', creatorIds);
        
        if (creatorsError) {
          console.error('Error fetching creators:', creatorsError);
          // Don't throw here, just log the error and continue with accounts without creator info
        }
        
        // Merge creators with accounts
        const accountsWithCreators = processedAccounts.map(account => ({
          ...account,
          creator: creators?.find(creator => creator.id === account.created_by)
        }));
        
        setAccounts(accountsWithCreators);
      } else {
        // No creators to fetch, just use the processed accounts
        setAccounts(processedAccounts);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to fetch accounts');
      // Set empty accounts array to prevent UI from breaking
      setAccounts([]);
      setTotalAccounts(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAccountStatus = async (accountId: string, newStatus: AccountStatus) => {
    if (updatingStatus) return; // Prevent multiple simultaneous updates
    
    setUpdatingStatus(accountId);
    try {
      // First fetch the account to ensure it exists
      const { data: account, error: fetchError } = await supabase
        .from('accounts')
        .select('*')
        .eq('id', accountId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch account: ${fetchError.message}`);
      }

      if (!account) {
        throw new Error('Account not found');
      }

      // Update the account status
      const { error: updateError } = await supabase
        .from('accounts')
        .update({ status: newStatus })
        .eq('id', accountId)
        .select()
        .single();

      if (updateError) {
        throw new Error(`Failed to update account status: ${updateError.message}`);
      }

      // Update local state after successful database update
      setAccounts(prevAccounts => 
        prevAccounts.map(acc => 
          acc.id === accountId 
            ? { ...acc, status: newStatus } 
            : acc
        )
      );

      // Log the status change
      await supabase.from('system_logs').insert({
        user_id: user?.id,
        action: 'UPDATE_ACCOUNT_STATUS',
        details: `Updated account ${account.email} status to ${newStatus}`
      });

      toast.success(`Account status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating account status:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update account status');
      
      // Refresh accounts to ensure UI matches database state
      await fetchAccounts(currentPage, pageSize);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDownloadAccount = async (account: AccountData) => {
    try {
      const cookiesResponse = await fetch(account.cookies_url);
      if (!cookiesResponse.ok) throw new Error('Failed to fetch cookies file');
      const cookiesContent = await cookiesResponse.text();

      const accountInfoContent = `================================================================
=   BEST ACCOUNTS BY AMAZON MIK | THANK YOU FOR PURCHASE  =
================================================================

Email - ${account.email}
Password - ${account.password}
Approx. orders - ${account.orders}
Reg. Date - ${account.registration_date}
SMS PIN: ${account.sms_pin}
Phone Number: ${account.phone_number}
Phone Number Expiry Date: ${account.phone_expiry}
Status: ${account.status}

*** NOTE ***
With recent changes, to get an SMS for the account you have to press "Get SMS" button in the main menu (/start) of our bot. Use information from this file to proceed.
Please contact us if you need more time for the phone number to work within one week before number ends it's working period.
We appreciate your business.`;

      const zip = new JSZip();
      zip.file('cookies.txt', cookiesContent);
      zip.file(`${account.email}.txt`, accountInfoContent);

      const zipContent = await zip.generateAsync({ type: 'blob' });
      const downloadUrl = URL.createObjectURL(zipContent);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${account.email} ${account.orders}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

    } catch (error) {
      console.error('Error downloading account:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to download account files');
    }
  };

  const handleDownloadBackup = async (account: AccountData) => {
    try {
      const response = await fetch(account.backup_url);
      if (!response.ok) throw new Error('Failed to fetch backup file');
      const blob = await response.blob();
      
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${account.email}_backup.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      
      toast.success('Backup downloaded successfully');
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to download backup file');
    }
  };

  const handleDeleteAccount = async (account: AccountData) => {
    if (!confirm('Are you sure you want to delete this account? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('accounts')
        .delete()
        .eq('id', account.id);

      if (error) throw error;

      await supabase.from('system_logs').insert({
        user_id: user?.id,
        action: 'DELETE_ACCOUNT',
        details: `Deleted account ${account.email}`
      });

      toast.success('Account deleted successfully');
      
      // Refresh the accounts list to update pagination
      fetchAccounts(currentPage, pageSize);
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete account');
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const resetFilters = () => {
    setStatusFilter('all');
    setOrderRangeFilter('all');
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setUserFilter('all');
    setIsFiltering(false);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Account Management</h2>
      
      {/* Filters */}
      <div className="mb-6 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 flex items-center">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </h3>
          
          {(statusFilter !== 'all' || orderRangeFilter !== 'all' || searchTerm || userFilter !== 'all') && (
            <button
              onClick={resetFilters}
              className="mt-2 md:mt-0 text-sm text-red-600 dark:text-red-400 flex items-center"
            >
              <X className="h-4 w-4 mr-1" />
              Clear Filters
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Status Filter */}
          <div>
            <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as AccountStatus | 'all');
                setCurrentPage(1);
                setIsFiltering(true);
              }}
              className="block w-full rounded-md border border-gray-300 py-1.5 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          
          {/* Order Range Filter */}
          <div>
            <label htmlFor="orderRangeFilter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Order Count
            </label>
            <select
              id="orderRangeFilter"
              value={orderRangeFilter}
              onChange={(e) => {
                setOrderRangeFilter(e.target.value as OrderRange);
                setCurrentPage(1);
                setIsFiltering(true);
              }}
              className="block w-full rounded-md border border-gray-300 py-1.5 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All Orders</option>
              <option value="1-10">1-10 Orders</option>
              <option value="10-50">10-50 Orders</option>
              <option value="50-100">50-100 Orders</option>
              <option value="100-200">100-200 Orders</option>
              <option value="200-500">200-500 Orders</option>
              <option value="500+">500+ Orders</option>
            </select>
          </div>

          {/* User Filter */}
          <div>
            <label htmlFor="userFilter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Created By
            </label>
            <select
              id="userFilter"
              value={userFilter}
              onChange={(e) => {
                setUserFilter(e.target.value);
                setCurrentPage(1);
                setIsFiltering(true);
              }}
              className="block w-full rounded-md border border-gray-300 py-1.5 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All Users</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name || user.email}
                </option>
              ))}
            </select>
          </div>
          
          {/* Search */}
          <div>
            <label htmlFor="searchTerm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search (Email or Phone)
            </label>
            <div className="relative">
              <input
                type="text"
                id="searchTerm"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setIsFiltering(true);
                }}
                placeholder="Search by email or phone..."
                className="block w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setCurrentPage(1);
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {accounts.length === 0 ? (
        <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-lg shadow">
          <p className="text-gray-500 dark:text-gray-400">
            {isFiltering ? 'No accounts match your filters' : 'No accounts found'}
          </p>
          {isFiltering && (
            <button
              onClick={resetFilters}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Created By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Orders</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Registration Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Phone Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Created At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {accounts.map((account) => (
                  <tr key={account.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <img
                          src={account.creator?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(account.creator?.name || account.creator?.email || 'Unknown')}&background=random`}
                          alt="Creator"
                          className="h-8 w-8 rounded-full object-cover"
                        />
                        <span className="ml-2 text-sm text-gray-900 dark:text-white">
                          {account.creator?.name || account.creator?.email || 'Unknown User'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{account.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{account.orders}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{account.registration_date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{account.phone_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {updatingStatus === account.id ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            account.status === 'completed' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          }`}>
                            {account.status}
                          </span>
                          <button
                            onClick={() => handleUpdateAccountStatus(account.id, account.status === 'completed' ? 'pending' : 'completed')}
                            className={`p-1 ${
                              account.status === 'completed'
                                ? 'text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300'
                                : 'text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300'
                            }`}
                            disabled={updatingStatus !== null}
                          >
                            {account.status === 'completed' ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{new Date(account.created_at).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="relative">
                        <button
                          onClick={() => setOpenActionMenuId(openActionMenuId === account.id ? null : account.id)}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                        
                        {openActionMenuId === account.id && (
                          <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5 z-10">
                            <div className="py-1" role="menu" aria-orientation="vertical">
                              <button
                                onClick={() => {
                                  handleDownloadAccount(account);
                                  setOpenActionMenuId(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center"
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Download
                              </button>
                              <button
                                onClick={() => {
                                  handleDownloadBackup(account);
                                  setOpenActionMenuId(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center"
                              >
                                <Upload className="h-4 w-4 mr-2" />
                                Backup
                              </button>
                              <button
                                onClick={() => {
                                  handleDeleteAccount(account);
                                  setOpenActionMenuId(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-6 px-4">
            <div className="flex items-center">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Showing <span className="font-medium">{totalAccounts > 0 ? (currentPage - 1) * pageSize + 1 : 0}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, totalAccounts)}</span> of{' '}
                <span className="font-medium">{totalAccounts}</span> accounts
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <div>
                <label htmlFor="pageSize" className="sr-only">Page Size</label>
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="block w-full rounded-md border-gray-300 py-1.5 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
              
              <nav className="flex items-center space-x-2" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-md text-sm font-medium ${
                    currentPage === 1
                      ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum = i + 1;
                    if (totalPages > 5) {
                      if (currentPage > 3) {
                        pageNum = currentPage - 2 + i;
                      }
                      if (pageNum > totalPages) {
                        pageNum = totalPages - (4 - i);
                      }
                    }
                    return (
                      <button
                        key={i}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                          currentPage === pageNum
                            ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-md text-sm font-medium ${
                    currentPage === totalPages
                      ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AccountsTab;